/**
 * <pdp-connected-colors> previews colour labels and warms sibling hero images.
 * Swatches remain ordinary links. `[data-color-image]` is an inert template whose
 * image uses the destination gallery's src/srcset/sizes and data-placeholder-src.
 * Idle warming is limited to two nearest siblings after the current hero loads.
 * Other colours warm once on pointer intent or focus, always with low priority.
 */
const IDLE_PRELOAD_LIMIT = 2;
const POINTER_INTENT_DELAY = 80;
const isTouchPointer = (event) => Boolean(event?.pointerType) && event.pointerType !== 'mouse';

class PdpConnectedColors extends HTMLElement {
  #label = null;
  #defaultName = '';
  #list = null;
  #swatches = [];
  #controller = null;
  #touchFocusSwatch = null;
  #preloadedImages = new Map();
  #intentTimer = null;
  #idleHandle = null;
  #idleUsesCallback = false;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#label = this.querySelector('[data-ref="color-label"]');
    this.#defaultName = this.dataset.defaultColorName || this.#label?.textContent?.trim() || '';
    this.#list = this.querySelector('[data-ref="connected-colors"]');

    this.#swatches = [...this.querySelectorAll('.pdp-connected-colors__swatch')];

    this.#swatches.forEach((swatch) => {
      swatch.addEventListener(
        'pointerenter',
        (event) => {
          if (isTouchPointer(event)) {
            this.#preload(swatch);
            return;
          }
          this.#touchFocusSwatch = null;
          this.#setLabel(swatch.dataset.colorName);
          this.#clearIntentTimer();
          this.#intentTimer = window.setTimeout(() => {
            this.#intentTimer = null;
            this.#preload(swatch);
          }, POINTER_INTENT_DELAY);
        },
        { signal }
      );
      swatch.addEventListener('pointerleave', () => this.#clearIntentTimer(), { signal });
      swatch.addEventListener(
        'pointerdown',
        (event) => {
          this.#touchFocusSwatch = isTouchPointer(event) ? swatch : null;
          this.#clearIntentTimer();
          this.#preload(swatch);
        },
        { signal, passive: true }
      );
      swatch.addEventListener(
        'focus',
        () => {
          this.#clearIntentTimer();
          this.#preload(swatch);
          // Touch can synthesize focus while leaving for another product. Keep
          // the current label steady; mouse hover and keyboard focus preview it.
          if (this.#touchFocusSwatch !== swatch) this.#setLabel(swatch.dataset.colorName);
        },
        { signal }
      );
    });

    this.addEventListener('keydown', () => (this.#touchFocusSwatch = null), { signal });
    this.#list?.addEventListener(
      'pointerleave',
      (event) => {
        if (!isTouchPointer(event)) this.#setLabel(this.#defaultName);
      },
      { signal }
    );
    this.#list?.addEventListener(
      'focusout',
      (event) => {
        if (!this.#list?.contains(event.relatedTarget)) this.#setLabel(this.#defaultName);
      },
      { signal }
    );

    if (!this.#canPreloadInBackground()) return;
    const hero = this.closest('product-component')?.querySelector(
      '.main-product__image--first:not(.main-product__image-placeholder)'
    );
    if (hero?.complete && hero.naturalWidth > 0) {
      this.#scheduleIdlePreload();
    } else {
      hero?.addEventListener('load', () => this.#scheduleIdlePreload(), { signal, once: true });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#clearIntentTimer();
    if (this.#idleHandle !== null) {
      if (this.#idleUsesCallback) window.cancelIdleCallback(this.#idleHandle);
      else window.clearTimeout(this.#idleHandle);
    }
    this.#idleHandle = null;
    this.#swatches = [];
    this.#preloadedImages.clear();
    this.#touchFocusSwatch = null;
    this.#setLabel(this.#defaultName);
    this.#list = null;
    this.#label = null;
    this.initialized = false;
  }

  #clearIntentTimer() {
    window.clearTimeout(this.#intentTimer);
    this.#intentTimer = null;
  }

  #canPreloadInBackground() {
    const connection = navigator.connection;
    return !connection?.saveData && !/(^|-)2g$/.test(connection?.effectiveType || '');
  }

  #scheduleIdlePreload() {
    const preloadNearest = () => {
      this.#idleHandle = null;
      if (!this.isConnected || !this.#canPreloadInBackground()) return;

      const selectedIndex = this.#swatches.findIndex((swatch) =>
        swatch.hasAttribute('aria-current')
      );
      const candidates = this.#swatches
        .map((swatch, index) => ({
          swatch,
          distance: Math.abs(index - Math.max(selectedIndex, 0)),
        }))
        .filter(({ swatch }) => swatch.querySelector('[data-color-image]'))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, IDLE_PRELOAD_LIMIT);
      candidates.forEach(({ swatch }) => this.#preload(swatch));
    };

    this.#idleUsesCallback = typeof window.requestIdleCallback === 'function';
    this.#idleHandle = this.#idleUsesCallback
      ? window.requestIdleCallback(preloadNearest)
      : window.setTimeout(preloadNearest, 200);
  }

  #preload(swatch) {
    if (this.#preloadedImages.has(swatch.href)) return;
    const source = swatch.querySelector('[data-color-image]')?.content.querySelector('img');
    if (!source) return;

    const hero = new Image();
    hero.fetchPriority = 'low';
    hero.decoding = 'async';
    hero.sizes = source.sizes;
    hero.srcset = source.srcset;
    hero.src = source.src;

    const placeholder = new Image();
    placeholder.fetchPriority = 'low';
    placeholder.decoding = 'async';
    placeholder.src = source.dataset.placeholderSrc;
    this.#preloadedImages.set(swatch.href, [hero, placeholder]);
  }

  #setLabel(text) {
    if (this.#label && text) this.#label.textContent = text;
  }
}

if (!customElements.get('pdp-connected-colors')) {
  customElements.define('pdp-connected-colors', PdpConnectedColors);
}
